import { Box, Flex, Grid, Icon, Text, useColorModeValue } from "@chakra-ui/react";
import React from "react";
import { useTranslation } from "react-i18next";
import Hero from "./components/Hero";
import StatBar from "./components/StatBar";
import NewProducts from "./components/NewProducts";
import Card from "../../../components/Card/Card";
import { products } from "data/products";

// Inline so each capability reads differently at a glance; the three cards were previously
// identical blocks of text.
const ClockIcon = (props) => (
  <Icon viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" {...props}>
    <circle cx="12" cy="12" r="8.2" />
    <path d="M12 7.4v4.6l3.1 1.8" strokeLinecap="round" strokeLinejoin="round" />
  </Icon>
);

const GridIcon = (props) => (
  <Icon viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" {...props}>
    <rect x="3.6" y="3.6" width="7" height="7" rx="1.2" />
    <rect x="13.4" y="3.6" width="7" height="7" rx="1.2" />
    <rect x="3.6" y="13.4" width="7" height="7" rx="1.2" />
    <rect x="13.4" y="13.4" width="7" height="7" rx="1.2" />
  </Icon>
);

const ShieldIcon = (props) => (
  <Icon viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" {...props}>
    <path
      d="M12 3.2l7.4 3.3v5.2c0 4.4-3 7.6-7.4 9.1-4.4-1.5-7.4-4.7-7.4-9.1V6.5z"
      strokeLinejoin="round"
    />
    <path d="M8.9 12.1l2.2 2.2 4-4.4" strokeLinecap="round" strokeLinejoin="round" />
  </Icon>
);

const CAPABILITIES = [
  { key: "since2002", icon: ClockIcon },
  { key: "diverseProducts", icon: GridIcon },
  { key: "provenTech", icon: ShieldIcon },
];

function CapabilityCard({ icon: IconComponent, title, description }) {
  const headingColor = useColorModeValue("#101A2E", "white");
  const bodyColor = useColorModeValue("#6B7A90", "gray.400");
  const borderColor = useColorModeValue("#E1E7F0", "whiteAlpha.200");

  return (
    <Card
      p="26px 24px 28px"
      border="1px solid"
      borderColor={borderColor}
      borderRadius="8px"
      boxShadow="none"
      h="100%"
    >
      <Flex
        w="40px"
        h="40px"
        flex="none"
        mb="18px"
        borderRadius="5px"
        align="center"
        justify="center"
        bgImage="linear-gradient(140deg, #2B8FD4, #19418F)"
      >
        <IconComponent w="21px" h="21px" color="white" />
      </Flex>
      <Text
        fontSize="17px"
        fontWeight="700"
        letterSpacing="-0.02em"
        color={headingColor}
        mb="9px"
      >
        {title}
      </Text>
      <Text fontSize="13.5px" lineHeight="1.68" color={bodyColor}>
        {description}
      </Text>
    </Card>
  );
}

function Main() {
  const { t } = useTranslation("home");
  const headingColor = useColorModeValue("#101A2E", "white");
  const bodyColor = useColorModeValue("#33415A", "gray.300");
  const borderColor = useColorModeValue("#E1E7F0", "whiteAlpha.200");

  // Model counts in the copy are derived from the catalogue so they cannot go stale.
  // Named modelCount rather than i18next's reserved `count`, which triggers plural keys.
  const modelCount = products.length;

  return (
    <Flex direction="column">
      {/* Cancels PanelContent (15px) + PanelContainer (15px) so the hero runs edge to edge. */}
      <Box mx="-30px" mb="30px">
        <Hero />
        <StatBar />
      </Box>

      <Card
        p={{ base: "26px", md: "32px" }}
        mb="26px"
        border="1px solid"
        borderColor={borderColor}
        borderRadius="8px"
        boxShadow="none"
      >
        <Text
          fontSize={{ base: "20px", md: "23px" }}
          fontWeight="700"
          letterSpacing="-0.028em"
          color={headingColor}
          mb="12px"
        >
          {t("companyTitle")}
        </Text>
        <Text fontSize="15px" color={bodyColor} lineHeight="1.8">
          {t("companyDescription", { modelCount })}
        </Text>
      </Card>

      <Grid
        templateColumns={{ base: "1fr", md: "repeat(2, 1fr)", lg: "repeat(3, 1fr)" }}
        gap="18px"
        mb="26px"
      >
        {CAPABILITIES.map(({ key, icon }) => (
          <CapabilityCard
            key={key}
            icon={icon}
            title={t(`featureCards.${key}.title`)}
            description={t(`featureCards.${key}.description`, { modelCount })}
          />
        ))}
      </Grid>

      <NewProducts />
    </Flex>
  );
}

export default Main;
